import { useCallback, useRef, useLayoutEffect } from 'react'

// Measures where the right-edge fade is allowed to paint and publishes it as
// one background layer per band.
// Each layer is a var(--lbv2-fade-gradient) reference, so the gradient itself
// stays in the stylesheet and only the geometry is measured here.

// Every skipped row is one cell spanning the whole table, which is how they are
// written here - matching on that keeps new ones covered without a class list.
const FULL_WIDTH_CELLS = 'td[colspan="100"]'
const HIDDEN_TABLE = '.lbv2__full-leaderboard-body.is-collapsed'

const LAYER = 'var(--lbv2-fade-gradient)'

// Grown by a pixel at each edge so a skipped row's own border, which sits
// exactly on the boundary, stays outside the neighbouring band.
const EDGE_GUARD_PX = 1

// Ordered input, so a row overlapping the previous one just moves the cursor on.
const bandsBetween = (skipped, height) => {
  const bands = []
  let cursor = 0

  skipped.forEach(([ top, bottom ]) => {
    if (top > cursor) {
      bands.push({ top: cursor, height: top - cursor })
    }

    cursor = Math.max(cursor, bottom)
  })

  if (cursor < height) {
    bands.push({ top: cursor, height: height - cursor })
  }

  return bands
}

const useFadeBands = (scopeRef) => {
  const publishedRef = useRef(null)

  const measure = useCallback(() => {
    const scope = scopeRef.current

    if (!scope) {
      return
    }

    const box = scope.getBoundingClientRect()
    const skipped = []

    scope.querySelectorAll(FULL_WIDTH_CELLS).forEach((cell) => {
      // Rows of the collapsed (mounted, hidden) full leaderboard have real
      // rects but no place on screen.
      if (cell.closest(HIDDEN_TABLE)) {
        return
      }

      const row = cell.parentElement

      if (!row) {
        return
      }

      const rect = row.getBoundingClientRect()

      if (!rect.height) {
        return
      }

      // Out to whole pixels, never in: rounding a fractional edge the other way
      // spills the neighbouring band a hairline over the row it must skip.
      skipped.push([
        Math.max(0, Math.floor(rect.top - box.top) - EDGE_GUARD_PX),
        Math.max(0, Math.ceil(rect.bottom - box.top) + EDGE_GUARD_PX),
      ])
    })

    skipped.sort((a, b) => a[0] - b[0])

    const bands = bandsBetween(skipped, Math.round(box.height))
    const positions = bands.map((band) => `0 ${band.top}px`).join(', ')

    const sizes = bands.map((band) => `100% ${band.height}px`).join(', ')
    const published = `${positions}|${sizes}`

    // Most passes find the bands exactly where they were.
    if (published === publishedRef.current) {
      return
    }

    publishedRef.current = published

    scope.style.setProperty('--lbv2-fade-layers', bands.map(() => LAYER).join(', ') || 'none')
    scope.style.setProperty('--lbv2-fade-positions', positions || '0 0')
    scope.style.setProperty('--lbv2-fade-sizes', sizes || '100% 0')
  }, [ scopeRef ])

  // Observed rather than driven off renders: the rows belong to AggregateRow,
  // and ResultScope does not re-render when one opens.
  //
  // Size is the only thing watched: a row moving while the table's total height
  // stays the same does not re-measure.
  useLayoutEffect(() => {
    const scope = scopeRef.current

    if (!scope) {
      return undefined
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(scope)

    return () => observer.disconnect()
  }, [ scopeRef, measure ])
}

export default useFadeBands
